import React from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import Panel from '@atlaskit/panel';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import {
  commentsInThread,
  CommentThread,
} from 'src/components/conversation/src/components/comment-tree';
import { toConversationId } from 'src/components/conversation-provider/utils/to-conversation';
import { LoadingStatus } from 'src/constants/loading-status';
import { useIntl } from 'src/hooks/intl';
import { getCommentTree } from 'src/redux/pull-request/selectors';
import * as pageStyles from 'src/sections/global/components/page.style';
import { getCommentsLoadingState } from 'src/selectors/conversation-selectors';

import { CommitConversation } from './commit-conversation';
import messages from './commit-page.i18n';
import * as styles from './commit-page.styled';

export const ConversationWrapper = styled.div({
  marginBottom: token('space.300'),
  paddingBottom: token('space.300'),
});

function commentCount(threads: CommentThread[]) {
  return threads.reduce(
    (acc, thread) => acc + commentsInThread(thread).length,
    0
  );
}

export const CommitGlobalConversation = () => {
  const intl = useIntl();

  const commentsLoadingStatus = useSelector(getCommentsLoadingState);
  const commentTree = useSelector(getCommentTree);
  const threads = commentTree.globalThreads();

  const firstThread: CommentThread | undefined = threads[0];
  const conversationId = firstThread
    ? toConversationId(firstThread.topComment)
    : undefined;
  const placeholder = intl.formatMessage(messages.commentsGlobalAddComment);

  const conversationsGlobalTitle = (
    <pageStyles.PanelHeader>
      <FormattedMessage
        {...messages.commentsGlobalTitle}
        values={{ count: commentCount(threads) }}
      />
    </pageStyles.PanelHeader>
  );

  if (commentsLoadingStatus === LoadingStatus.Fetching)
    return (
      <styles.SpinnerWrapper>
        <Spinner size="medium" />
      </styles.SpinnerWrapper>
    );

  return (
    <styles.CommentWrapper
      data-qa="conversations-global-style"
      aria-label={intl.formatMessage(messages.globalConversationLabel)}
    >
      <Panel header={conversationsGlobalTitle} isDefaultExpanded>
        <ConversationWrapper>
          <CommitConversation
            loginLinkText={intl.formatMessage(messages.loginLinkText)}
            placeholder={placeholder}
            portalParentId={`portal-parent-pr-global-conversation-${conversationId}`}
            renderCommentWithReplyMention={false}
            commentThreads={threads}
          />
        </ConversationWrapper>
      </Panel>
    </styles.CommentWrapper>
  );
};
